import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import supabase from '../../../config/supabaseClient';
import { Key, Eye, EyeOff, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [debugInfo, setDebugInfo] = useState('');

  useEffect(() => {
    const checkResetSession = async () => {
      try {
        // Função para extrair parâmetros do hash da URL
        const getHashParams = () => {
          const hash = window.location.hash.substring(1);
          const params = new URLSearchParams(hash);
          return Object.fromEntries(params.entries());
        };

        // Obter parâmetros do hash e query string
        const hashParams = getHashParams();
        const queryParams = Object.fromEntries(searchParams.entries());
        
        // Debug: Mostrar todos os parâmetros da URL
        const urlInfo = `URL: ${window.location.href}\nQuery Params: ${JSON.stringify(queryParams, null, 2)}\nHash Params: ${JSON.stringify(hashParams, null, 2)}`;
        setDebugInfo(urlInfo);
        console.log('URL atual:', window.location.href);
        console.log('Query Params:', queryParams);
        console.log('Hash Params:', hashParams);

        // Verificar se há tokens no hash ou query string
        const accessToken = hashParams.access_token || queryParams.access_token;
        const refreshToken = hashParams.refresh_token || queryParams.refresh_token;
        const error = hashParams.error || queryParams.error;
        const errorDescription = hashParams.error_description || queryParams.error_description;
        const type = hashParams.type || queryParams.type;

        console.log('Tokens encontrados:', { accessToken: !!accessToken, refreshToken: !!refreshToken, error, type });

        if (error) {
          console.error('Erro na URL:', error, errorDescription);
          toast.error(`Erro: ${errorDescription || error}`);
          navigate('/login');
          return;
        }

        if (accessToken && refreshToken) {
          console.log('Definindo sessão com tokens...');
          // Definir sessão com os tokens
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (sessionError) {
            console.error('Erro ao definir sessão:', sessionError);
            toast.error('Link de reset inválido ou expirado');
            navigate('/login');
          } else {
            console.log('Sessão definida com sucesso');
            setSessionReady(true);
            toast.success('Link válido! Defina sua nova senha.');
          }
        } else {
          console.log('Tokens não encontrados na URL, verificando sessão existente...');
          // Verificar se já existe uma sessão (usuário pode ter clicado no link novamente)
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          console.log('Sessão existente:', { hasSession: !!session, error: sessionError });
          
          if (session && !sessionError) {
            console.log('Usando sessão existente');
            setSessionReady(true);
            toast.success('Sessão ativa encontrada. Defina sua nova senha.');
          } else {
            console.log('Nenhuma sessão válida encontrada');
            toast.error('Link de reset inválido ou expirado. Tente solicitar um novo reset.');
            navigate('/login');
          }
        }
      } catch (error) {
        console.error('Erro ao verificar sessão de reset:', error);
        toast.error('Erro ao processar link de reset');
        navigate('/login');
      } finally {
        setCheckingSession(false);
      }
    };

    checkResetSession();

    // Listener para mudanças de autenticação
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, !!session);
      
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        console.log('Password recovery session detected');
        setSessionReady(true);
        setCheckingSession(false);
        toast.success('Link válido! Defina sua nova senha.');
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      toast.success('Senha alterada com sucesso!');
      
      // Redirecionar para login após 2 segundos
      setTimeout(() => {
        navigate('/login');
      }, 2000);

    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      toast.error('Erro ao alterar senha: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Mostrar loading enquanto verifica a sessão
  if (checkingSession) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <Loader size={48} className="loading-spinner" />
            <h1>Verificando Link</h1>
            <p>Aguarde enquanto verificamos seu link de reset...</p>
            {debugInfo && (
              <div style={{ 
                marginTop: '20px', 
                padding: '10px', 
                background: '#f5f5f5', 
                borderRadius: '4px', 
                fontSize: '12px', 
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                <strong>Debug Info:</strong><br />
                {debugInfo}
              </div>
            )}
          </div>
        </div>
        <style jsx>{`
          .loading-spinner {
            animation: spin 1s linear infinite;
            color: #667eea;
            margin-bottom: 16px;
          }
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          .reset-password-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            padding: 20px;
          }

          .reset-password-card {
            background: white;
            border-radius: 12px;
            padding: 40px;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
            width: 100%;
            max-width: 400px;
          }

          .reset-password-header {
            text-align: center;
            margin-bottom: 30px;
          }

          .reset-password-header h1 {
            margin: 0 0 8px 0;
            color: #333;
            font-size: 24px;
          }

          .reset-password-header p {
            margin: 0;
            color: #666;
            font-size: 14px;
          }
        `}</style>
      </div>
    );
  }

  // Se a sessão não está pronta, não mostrar o formulário
  if (!sessionReady) {
    return null;
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-header">
          <Key size={48} />
          <h1>Redefinir Senha</h1>
          <p>Digite sua nova senha abaixo</p>
        </div>

        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="form-group">
            <label htmlFor="password">Nova Senha</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite sua nova senha"
                required
                minLength={6}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Nova Senha</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirme sua nova senha"
                required
                minLength={6}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="reset-password-button"
            disabled={loading || !password || !confirmPassword}
          >
            {loading ? 'Alterando...' : 'Alterar Senha'}
          </button>
        </form>
      </div>

      <style jsx>{`
        .reset-password-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 20px;
        }

        .reset-password-card {
          background: white;
          border-radius: 12px;
          padding: 40px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
          width: 100%;
          max-width: 400px;
        }

        .reset-password-header {
          text-align: center;
          margin-bottom: 30px;
        }

        .reset-password-header svg {
          color: #667eea;
          margin-bottom: 16px;
        }

        .reset-password-header h1 {
          margin: 0 0 8px 0;
          color: #333;
          font-size: 24px;
        }

        .reset-password-header p {
          margin: 0;
          color: #666;
          font-size: 14px;
        }

        .reset-password-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group label {
          font-size: 14px;
          font-weight: 500;
          color: #333;
        }

        .password-input-container {
          position: relative;
          display: flex;
          align-items: center;
        }

        .password-input-container input {
          width: 100%;
          padding: 12px 40px 12px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s;
        }

        .password-input-container input:focus {
          outline: none;
          border-color: #667eea;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          cursor: pointer;
          color: #666;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .password-toggle:hover {
          color: #333;
        }

        .reset-password-button {
          padding: 12px 20px;
          background: #667eea;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .reset-password-button:hover:not(:disabled) {
          background: #5a6fd8;
        }

        .reset-password-button:disabled {
          background: #ccc;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default ResetPassword;